<template>
  <div
    v-if="
      freebies.length && freebies.some((freebie) => freebie.products.length > 0)
    "
    class="cart-freebies"
    :class="{
      hidden: freebiesSoldout,
    }"
  >
    <button
      v-if="!isFreebieOpen"
      class="cart-freebies__prompt"
      type="button"
      @click="handlePromptClick"
    >
      <span
        class="cart-freebies__prompt-title text-mobile-paragraph-s-bold text-desktop-paragraph-s-bold text-left"
        v-text="promptStrings.title"
      />

      <span
        class="cart-freebies__prompt-subtitle text-mobile-paragraph-xs-regular text-desktop-paragraph-xs-regular text-left"
        v-text="promptStrings.subtitle"
      />

      <icon-plus class="cart-freebies__prompt-icon" />
    </button>

    <template v-else>
      <div
        class="cart-freebies__queue-title text-mobile-paragraph-m-bold text-desktop-paragraph-m-bold"
      >
        <span v-text="queueTitle" />
        <button
          type="button"
          :aria-expanded="!isFreebieOpen"
          class="cart-freebies__queue-close icon-button"
          @click="closeFreebie"
        >
          <icon-close />
        </button>
      </div>
      <!--
        we need to make all classes conditional for when we
        only have one active freebie
      -->
      <div
        :class="{
          'vertical splide splide--ttb splide--draggable':
            adjustedFreebies.length > 1,
        }"
        js-freebie-carousel="vertical"
      >
        <div
          :class="{
            splide__track: adjustedFreebies.length > 1,
          }"
        >
          <div
            :class="{
              splide__list: adjustedFreebies.length > 1,
            }"
          >
            <template
              v-for="(freebie, freebieIndex) of adjustedFreebies"
              :key="freebie.handle"
            >
              <div class="splide__slide">
                <div
                  :js-freebie-carousel="'freebie-' + freebieIndex"
                  class="splide cart-freebies__queue-slider"
                >
                  <div class="cart-freebies__queue-overflow splide__track">
                    <div class="cart-freebies__queue splide__list">
                      <div
                        v-for="(product, productIndex) of freebie.products"
                        :key="
                          product
                            ? `${product.handle}-${productIndex}`
                            : `loading-${productIndex}`
                        "
                        class="cart-freebies__queue-product-container splide__slide"
                      >
                        <cart-freebie-card
                          class="cart-freebies__queue-product"
                          :name="freebieName(freebie.handle, productIndex)"
                          :product="product"
                          :queue-limit="freebie.quantity"
                          :queue-size="
                            freebie.products.filter(
                              (product) => product.selected,
                            ).length
                          "
                          :selected="product.selected"
                          :show-checkbox="freebie.quantity > 1"
                          @select-product="
                            (variantId, selected) => {
                              selectProduct(variantId, freebie, selected);
                            }
                          "
                        />
                      </div>
                    </div>

                    <div :class="'splide__arrows horizontal__' + freebieIndex">
                      <button
                        :class="
                          'splide__arrow splide__arrow--prev horizontal__' +
                          freebieIndex +
                          '--prev'
                        "
                        type="button"
                      >
                        <span
                          class="visually-hidden"
                          v-text="$string('accessibility.carousel.previous')"
                        />

                        <icon-arrow-left />
                      </button>

                      <button
                        :class="
                          'splide__arrow splide__arrow--next horizontal__' +
                          freebieIndex +
                          '--next'
                        "
                        type="button"
                      >
                        <span
                          class="visually-hidden"
                          v-text="$string('accessibility.carousel.next')"
                        />

                        <icon-arrow-right />
                      </button>
                    </div>

                    <div
                      v-if="freebie.products.length > 1"
                      class="cart-freebies__progress"
                    >
                      <div
                        :ref="'progress-' + freebieIndex"
                        class="cart-freebies__progress-bar"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>

      <p
        v-if="error"
        class="cart-freebies__error text-mobile-paragraph-s-regular text-desktop-paragraph-s-regular"
        v-text="error"
      />

      <notification-panel
        v-if="error"
        class-container="cart-freebies__error"
        :show-close="false"
        :text="error"
        type="error"
      />

      <div class="cart-freebies__queue-footer text-center">
        <btn
          class="cart-freebies__queue-save"
          :label="$string('cart.freebies.save')"
          modifiers="secondary"
          :show-state-icon="state === 'updating'"
          size="medium"
          type="button"
          @click="handleSaveClick"
        >
          <template #stateIcon>
            <icon-loading />
          </template>
        </btn>
      </div>
    </template>
  </div>
</template>

<script>
/**
 * Vue: Cart freebies (cart-freebies)
 * -----------------------------------------------------------------------------
 * Component to handle loading and choosing campaign freebies.
 *
 *
 */
import { Splide } from '@splidejs/splide';
import { mapActions, mapState } from 'pinia';

import Btn from '~global/btn/btn';
import CartFreebieCard from '~async/cart-freebies/cart-freebie-card';
import NotificationPanel from '~async/utils/notification-panel/notification-panel';

import IconLoading from '~icons/general/loading.svg';
import IconPlus from '~icons/directional-navigation/plus.svg';
import IconArrowLeft from '~icons/directional-navigation/arrow/left.svg';
import IconArrowRight from '~icons/directional-navigation/arrow/right.svg';
import IconClose from '~icons/directional-navigation/close.svg';
import timings from '~/config/timings';
import { values as breakpoints } from '~/config/breakpoints';

import useCartStore from '~stores/cart';
import useDiscountCampaignsStore from '~stores/discount-campaigns';

export default {
  name: 'CartFreebies',

  components: {
    Btn,
    CartFreebieCard,
    IconLoading,
    IconPlus,
    IconArrowLeft,
    IconClose,
    IconArrowRight,
    NotificationPanel,
  },

  data() {
    return {
      carousel: null,
      error: '',
      freebies: [],
      state: 'loading',
      sameFreebiesSeveralTimes: false,
      currentAvailableQuantity: 0,
      automatedSlideChange: false,
      freebiesSoldout: false,
      slideIndex: 0,
      depositItemIds: [],
      isFreebieOpen: false,
    };
  },

  computed: {
    ...mapState(useDiscountCampaignsStore, {
      thresholds: 'thresholds',
    }),
    ...mapState(useCartStore, {
      cartItems: (store) => store.response.items,
      depositItemUpdate: 'depositItemUpdate',
    }),

    /**
     * Compute freebie products.
     * first check if a product of freebies is available
     * when there is only one product in freebies and quantity is more than 1
     * we need to render the freebie product several times
     * when there are multiple thresholds met
     * we need to merge the products from all thresholds into the products together
     * @returns {Array}
     */
    adjustedFreebies() {
      return this.freebies
        .map((freebie) => {
          if (freebie.quantity > 1 && freebie.products.length === 1) {
            this.sameFreebiesSeveralTimes = true;
            // Duplicate each product according to freebie.quantity
            // we start at 1 as one product is already added
            for (let quantity = 1; quantity < freebie.quantity; quantity++) {
              freebie.products.push(...freebie.products);
            }
          }
          this.currentAvailableQuantity = freebie.quantity;

          // if there are multiple thresholds met adapt quantity
          if (this.freebies.length > 1) {
            this.currentAvailableQuantity = this.freebies.reduce(
              (total, free) => {
                return total + free.quantity;
              },
              0,
            );
          }

          return freebie;
        })
        .filter((freebie) => freebie.products.length > 0);
    },

    cartFreebies() {
      return this.cartItems.filter((item) => {
        return item.properties?.[this.$variable('discount.freebies.property')];
      });
    },

    /**
     * Get prompt strings.
     * we go through all active freebies and sum up the quantity
     */
    promptStrings() {
      const count = this.cartFreebies.length;
      let limit = 0;

      this.adjustedFreebies.forEach((freebie) => {
        limit += freebie.quantity;
      });

      let title = this.$string('cart.freebies.prompt.title', {
        pluralise: limit,
      });

      if (count === limit) {
        title = this.$string('cart.freebies.prompt.edit_title', {
          pluralise: limit,
        });
      }

      const subtitle = this.$string('cart.freebies.prompt.subtitle', {
        replace: { count, total: limit },
      });

      return {
        subtitle,
        title,
      };
    },

    /**
     * Get queue title string.
     */
    queueTitle() {
      if (this.freebies.length === 0) {
        return false;
      }

      let count = 0;
      let total = 0;

      this.adjustedFreebies.forEach((freebie) => {
        count += freebie.products.filter((product) => product.selected).length;
        total += freebie.quantity;
      });

      return this.$string('cart.freebies.title', {
        pluralise: this.freebies[this.slideIndex]?.quantity || 1,
        replace: {
          count,
          total,
        },
      });
    },
  },
  watch: {
    /**
     * Watch thresholds to set freebies array.
     * and adapt tagline
     */
    thresholds: {
      handler() {
        this.isFreebieOpen = false;
        this.setFreebies();
        this.currentAvailableQuantity = 0;
      },
      immediate: true,
    },
  },

  mounted() {
    this.slideIndex = 0;
  },

  methods: {
    ...mapActions(useCartStore, {
      addItem: 'addItem',
      removeItem: 'updateItem',
      setDepositItemUpdate: 'setDepositItemUpdate',
    }),

    freebieName(handle, freebieIndex) {
      return `${handle}-${freebieIndex}`;
    },

    /**
     * Construct vertical carousel for freebies.
     */
    constructVerticalCarousel() {
      const verticalSelector = `#vue-element [js-freebie-carousel="vertical"]`;

      // check if there is only one freebie then only build a simple carousel
      if (this.adjustedFreebies.length === 1) {
        this.constructCarousel();
        return;
      }

      this.carousel = new Splide(verticalSelector, {
        direction: 'ttb',
        height: '10rem',
        arrows: false,
        wheel: true,
        waitForTransition: true,
        wheelSleep: 500,
        drag: true,
        gap: 'var(--spacing-s)',
        breakpoints: {
          [breakpoints.m]: {
            height: '8rem',
          },
        },
      });

      // when the active slide changes, we change the title
      this.carousel.on('active', (slide) => {
        this.slideIndex = slide.index;
        this.changePagination();
      });

      this.carousel.on('visible', (slide) => {
        const index = slide.index;

        // if the vertical carousel exists we check if the quantity is the same
        // as the queue length and move the vertical carousel to the next slide
        if (
          this.freebies[index]?.products.filter((product) => product.selected)
            .length === this.freebies[index]?.quantity &&
          this.carousel &&
          !this.automatedSlideChange
        ) {
          this.carousel.go('>');
          this.automatedSlideChange = true;
        }
      });

      this.constructCarousel();
    },

    /**
     * Scroll pagination into view when outside of viewport
     */
    changePagination() {
      const pagination = document.querySelector(
        `#vue-element [js-freebie-carousel="vertical"] .splide__pagination`,
      );
      const activePagination = pagination?.querySelector('.is-active');

      if (activePagination) {
        activePagination.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    },
    /**
     * Construct carousel for freebies
     */
    constructCarousel() {
      this.adjustedFreebies.forEach((freebie, index) => {
        const selector = `#vue-element [js-freebie-carousel="freebie-${index}"]`;

        if (!selector) {
          return;
        }

        const carousel = new Splide(selector, {
          arrows: true,
          drag: true,
          height: '6rem',
          focus: false,
          gap: 'var(--spacing-s)',
          mediaQuery: 'min',
          omitEnd: true,
          padding: { left: 0, right: 'var(--layout-margin)' },
          pagination: false,
          perMove: 1,
          perPage: 1.5,
          slideFocus: false,
          type: 'slide',
          breakpoints: {
            [breakpoints.m]: {
              arrows: true,
              drag: false,
              gap: 'var(--spacing-xs)',
              padding: 0,
              perPage: 2,
              height: '8rem',
            },
          },
        });

        // when the slide changes, we adjust the progress bar
        carousel.on('mounted move', () => {
          const progress = this.$refs[`progress-${index}`];
          const end = carousel.Components?.Controller?.getEnd() + 1;
          const rate = Math.min((carousel.index + 1) / end, 1);
          if (!progress) {
            return;
          }
          progress[0].style.width = `${String(100 * rate)}%`;
        });

        // only execute the mount function after the first carousel is mounted
        setTimeout(() => {
          if (index === 1 && this.carousel) {
            this.carousel.mount();
          }
          carousel.mount();
        }, timings.normal);
      });
    },

    /**
     * Set freebies.
     */
    setFreebies() {
      this.freebies = this.thresholds
        .filter((threshold) => {
          // if all products are nor available we don't show the freebie
          const visibleProducts = threshold.freeProducts.filter((product) => {
            const availableState = product.selectedOrFirstAvailableVariant
              ? product.selectedOrFirstAvailableVariant.availableState
              : product.availableState;
            return (
              product.availableState !== 'hidden' &&
              !(
                this.$variable('discount.freebies.hideSoldout') &&
                availableState !== 'available'
              )
            );
          });

          const allSoldOut = visibleProducts.every((product) => {
            const availableState = product.selectedOrFirstAvailableVariant
              ? product.selectedOrFirstAvailableVariant.availableState
              : product.availableState;
            return availableState !== 'available';
          });

          return (
            (threshold.type === 'threshold' || threshold.type === 'product') &&
            threshold.active &&
            !allSoldOut
          );
        })
        .map((threshold) => {
          const products = threshold.freeProducts
            .sort((a, b) => {
              if (
                !a.selectedOrFirstAvailableVariant?.available &&
                b.selectedOrFirstAvailableVariant?.available
              ) {
                return 1;
              }

              if (
                a.selectedOrFirstAvailableVariant?.available &&
                !b.selectedOrFirstAvailableVariant?.available
              ) {
                return -1;
              }

              return 0;
            })
            .map((product) => {
              let selected = false;
              this.cartFreebies.forEach((freebie) => {
                const freebieHandle =
                  freebie.properties?.[
                    this.$variable('discount.freebies.property')
                  ];

                if (freebieHandle === threshold.handle) {
                  selected =
                    selected ||
                    freebie.id === product.selectedOrFirstAvailableVariant?.id;
                }
              });

              const updatedProduct = {
                ...product,
                selected,
              };

              return updatedProduct;
            });

          this.state = 'ready';

          return {
            ...threshold,
            products,
            queue: [],
            thresholdGroup: [],
          };
        });
    },

    /**
     * Handle freebie prompt click (open).
     * runs the mapping in a race to create the carousel quicker as we only
     * need to wait for the first promise
     */
    async handlePromptClick() {
      // Set open property before async actions
      this.isFreebieOpen = true;
      // Construct the carousel when at least one freebie is processed
      await this.$nextTick();
      this.constructVerticalCarousel();
    },

    selectProduct(variantId, freebie, selected) {
      const unselectOthers = freebie.quantity === 1 && selected;
      freebie.products.forEach((freebieProduct) => {
        if (freebieProduct.selectedOrFirstAvailableVariant.id === variantId) {
          freebieProduct.selected = selected;
        } else if (unselectOthers) {
          freebieProduct.selected = false;
        }
      });

      if (
        freebie.products.filter((product) => product.selected).length ===
          freebie.quantity &&
        this.carousel
      ) {
        this.carousel.go('>');
        this.automatedSlideChange = true;
      }
    },

    /**
     * Reset the queue and close the freebie
     */
    closeFreebie() {
      this.freebies.forEach((freebie) => {
        freebie.queue = [];
        this.automatedSlideChange = false;
      });
      this.isFreebieOpen = false;
    },

    /**
     * Handle save click.
     * Uses promise to avoid race-condition in adding/removing
     * @param {Object} freebie - Current freebie.
     */
    async handleSaveClick() {
      const freebiesToRemove = [];
      const freebiesToAdd = [];

      /* Check if products are in cart, that are no longer selected
       * these have to be removed
       * also checks for deposit
       */

      this.cartFreebies.forEach((cartFreebie) => {
        const fittingFreebie = this.freebies.find(
          (freebie) =>
            freebie.handle ===
            cartFreebie.properties?.[
              this.$variable('discount.freebies.property')
            ],
        );

        const selectedProduct = fittingFreebie.products.find(
          (product) =>
            product.selected &&
            product.selectedOrFirstAvailableVariant.id === cartFreebie.id,
        );

        if (!selectedProduct) {
          freebiesToRemove.push({
            id: cartFreebie.key,
            quantity: 0,
            deposit:
              cartFreebie.can_deposit &&
              this.$variable('store.country') === 'DE'
                ? cartFreebie.can_deposit_amount * cartFreebie.quantity
                : 0,
          });
        }
      });

      /* Check if products that are selected are not in the cart
       * these have to be added
       * also checks for deposit
       */
      this.freebies.forEach((freebie) => {
        const fittingCartItems = this.cartFreebies.filter(
          (cartFreebie) =>
            cartFreebie.properties?.[
              this.$variable('discount.freebies.property')
            ] === freebie.handle,
        );

        freebie.products.forEach((product) => {
          const selectedOrFirstAvailableVariant =
            product.selectedOrFirstAvailableVariant;

          if (
            product.selected &&
            !fittingCartItems.some(
              (item) => item.id === selectedOrFirstAvailableVariant.id,
            )
          ) {
            freebiesToAdd.push({
              properties: {
                [this.$variable('discount.freebies.property')]: freebie.handle,
              },
              quantity: 1,
              id: selectedOrFirstAvailableVariant.id,
              deposit:
                selectedOrFirstAvailableVariant.canDeposit &&
                this.$variable('store.country') === 'DE'
                  ? selectedOrFirstAvailableVariant.canDepositAmount
                  : 0,
            });
          }
        });
      });

      const depositAmountToRemove = freebiesToRemove.reduce(
        (sum, item) => sum + (item.deposit || 0),
        0,
      );

      const depositAmountToAdd = freebiesToAdd.reduce(
        (sum, item) => sum + (parseInt(item.deposit) || 0),
        0,
      );

      const depositAmount = depositAmountToAdd - depositAmountToRemove;
      let depositProductIds = [];

      if (depositAmount !== 0) {
        depositProductIds = [...freebiesToRemove, ...freebiesToAdd]
          .filter((item) => item.deposit > 0)
          .map((item) => item.id);

        this.setDepositItemUpdate([
          ...this.depositItemUpdate,
          ...depositProductIds,
        ]);
      }

      if (freebiesToRemove.length + freebiesToAdd.length > 0) {
        this.state = 'updating';
      }

      // first we need to remove, so we don't have double items in the cart
      if (freebiesToRemove.length > 0) {
        await this.removeItem(freebiesToRemove);
      }

      if (freebiesToAdd.length > 0) {
        await this.addItem(freebiesToAdd);
      }

      this.isFreebieOpen = false;

      if (depositAmount !== 0) {
        if (depositAmount > 0) {
          await this.addItem([
            {
              id: this.$variable('product.depositProductID'),
              quantity: depositAmount,
            },
          ]);
        } else {
          const cartDepositItem = this.cartItems.find(
            (item) =>
              item.id === parseInt(this.$variable('product.depositProductID')),
          );
          await this.removeItem([
            {
              id: cartDepositItem.key,
              quantity: cartDepositItem.quantity + depositAmount,
            },
          ]);
        }

        this.setDepositItemUpdate(
          this.depositItemUpdate.filter(
            (id) => !depositProductIds.includes(id),
          ),
        );
      }

      this.state = 'ready';
    },

    /**
     * Handle error message.
     * @param {Object} errorDescription - Error description from add request.
     * @returns {String}
     */
    handleErrorMessage(errorDescription) {
      if (typeof errorDescription === 'string') {
        return errorDescription;
      }

      return Object.values(errorDescription)
        .flat()
        .map((error) => {
          return error.endsWith('.') ? error : `${error}.`;
        })
        .join('<br>');
    },
  },
};
</script>

<style lang="scss">
@import './cart-freebies';
</style>
